import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash } from 'react-icons/bs';
import { toast } from 'react-toastify';
import AppointmentService from '../services/AppointmentService';
import AppointmentParametersService from '../services/AppointmentParametersService';
import XPortionService from '../services/XPortionService';
import userService from '../services/userService';
import ProfileImage from './ProfileImage';
import UserPositionService from '../services/UserPositionService';
import OrganizationalUnitTypeService from '../services/OrganizationalUnitTypeService';
import OrganizationalUnitService from '../services/OrganizationalUnitService';
import { NonOrgPositionService } from '../services/NonOrgPositionService';
import ResponsiveTable from './ResponsiveTable';

// Helper function to find the closest matching non-organizational position
const findClosestNonOrgPosition = (targetTitle, availablePositions) => {
    console.log('Fuzzy matching for:', targetTitle);
    
    if (!targetTitle || !availablePositions || availablePositions.length === 0) {
        console.log('No target title or available positions');
        return null;
    }

    let bestMatch = null;
    let bestScore = 0;
    const targetLower = targetTitle.toLowerCase();
    console.log('Target (lowercase):', targetLower);

    availablePositions.forEach(position => {
        const positionLower = position.name.toLowerCase();
        let score = 0;
        
        console.log(`\nChecking: "${position.name}" (${positionLower})`);

        // Exact match (should not happen here, but just in case)
        if (positionLower === targetLower) {
            score = 100;
            console.log('  Exact match! Score: 100');
        }
        // Check if position name is contained in target or vice versa
        else if (targetLower.includes(positionLower) || positionLower.includes(targetLower)) {
            score = 80;
            console.log('  Contains match! Score: 80');
        }
        // Check for word matches
        else {
            const targetWords = targetLower.split(/[\s&-]+/).filter(word => word.length > 2);
            const positionWords = positionLower.split(/[\s&-]+/).filter(word => word.length > 2);
            
            console.log('  Target words:', targetWords);
            console.log('  Position words:', positionWords);
            
            let wordMatches = 0;
            targetWords.forEach(targetWord => {
                positionWords.forEach(positionWord => {
                    if (targetWord === positionWord) {
                        wordMatches++;
                        console.log(`    Exact word match: "${targetWord}"`);
                    } else if (targetWord.includes(positionWord) || positionWord.includes(targetWord)) {
                        wordMatches += 0.5;
                        console.log(`    Partial word match: "${targetWord}" ~ "${positionWord}"`);
                    }
                });
            });
            
            if (wordMatches > 0) {
                score = (wordMatches / Math.max(targetWords.length, positionWords.length)) * 70;
                console.log(`  Word matches: ${wordMatches}, Score: ${score}`);
            }
        }

        // Check if it starts with similar text
        if (score > 0) {
            const commonPrefixLength = getCommonPrefixLength(targetLower, positionLower);
            if (commonPrefixLength > 3) {
                const prefixBonus = (commonPrefixLength / Math.max(targetLower.length, positionLower.length)) * 20;
                score += prefixBonus;
                console.log(`  Prefix bonus: ${prefixBonus}, Total score: ${score}`);
            }
        }

        console.log(`  Final score for "${position.name}": ${score}`);

        if (score > bestScore) {
            bestScore = score;
            bestMatch = position;
            console.log(`  New best match: "${position.name}" with score ${score}`);
        }
    });

    console.log(`\nBest match overall: "${bestMatch?.name || 'None'}" with score ${bestScore}`);
    
    // Only return a match if the score is reasonable (above 30%)
    const result = bestScore > 30 ? bestMatch : null;
    console.log('Returning:', result?.name || 'null');
    return result;
};

// Helper function to get common prefix length
const getCommonPrefixLength = (str1, str2) => {
    let length = 0;
    const minLength = Math.min(str1.length, str2.length);
    for (let i = 0; i < minLength; i++) {
        if (str1[i] === str2[i]) {
            length++;
        } else {
            break;
        }
    }
    return length;
};


const Appointment = () => {
    const [appointments, setAppointments] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    
    // Additional state for appointment parameters
    const [employmentTypes, setEmploymentTypes] = useState([]);
    const [experienceLevels, setExperienceLevels] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [organizationalLevels, setOrganizationalLevels] = useState([]);
    const [latestXPortion, setLatestXPortion] = useState(null);

    // Add new state for user positions
    const [userPositions, setUserPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState(null);

    const [nonOrgPositions, setNonOrgPositions] = useState([]);
    const [positionType, setPositionType] = useState('organizational'); // 'organizational' or 'nonOrganizational'
    const [selectedNonOrgPosition, setSelectedNonOrgPosition] = useState(null);

    const [formData, setFormData] = useState({
        userId: '',
        organizationalUnitName: '',
        roleName: '',
        positionTitle: '',
        employmentType: '',
        employmentTypeMultiplier: 1.0,
        positionMultiplier: 1.0,
        experienceLevelName: '',
        experienceLevelMultiplier: 1.0,
        educationLevelName: '',
        educationlevelMultiplier: 1.0,
        organizationalLevelName: '',
        organizationalLevelMultiplier: 1.0,
        roleImportance: 0,
        impactFactor: 1.0,
        xPortion: 1.0,
        startDate: '',
        endDate: ''
    });

    // Fetch appointments, users, and parameters data
    useEffect(() => {
        fetchData();
        fetchAppointmentParameters();
        fetchNonOrgPositions();
    }, []);

    const fetchData = async () => {
        try {
            const [appointmentsData, usersData] = await Promise.all([
                AppointmentService.getAllAppointments(),
                userService.getAllUsers()
            ]);
            setAppointments(appointmentsData);
            setUsers(usersData);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load appointments');
            setLoading(false);
        }
    };

    const fetchAppointmentParameters = async () => {
        try {
            const [empTypes, expLevels, eduLevels, orgLevels, xPortion] = await Promise.all([
                AppointmentParametersService.getAllEmploymentTypes(),
                AppointmentParametersService.getAllExperienceLevels(),
                AppointmentParametersService.getAllEducationLevels(),
                AppointmentParametersService.getAllOrganizationalLevels(),
                XPortionService.getLatest()
            ]);

            setEmploymentTypes(empTypes || []);
            setExperienceLevels(expLevels || []);
            setEducationLevels(eduLevels || []);
            setOrganizationalLevels(orgLevels || []);
            setLatestXPortion(xPortion);

            // Update form data with latest XPortion value
            setFormData(prev => ({
                ...prev,
                xPortion: xPortion?.value ?? 1.0
            }));
        } catch (err) {
            console.error('Error fetching appointment parameters:', err);
            toast.error('Failed to load appointment parameters');
        }
    };

    const fetchNonOrgPositions = async () => {
        try {
            const positions = await NonOrgPositionService.getAll();
            setNonOrgPositions(positions);
        } catch (error) {
            console.error('Error fetching non-organizational positions:', error);
            toast.error('Failed to load non-organizational positions');
        }
    };

    // Add new function to fetch user positions
    const fetchUserPositions = async (userId) => {
        try {
            const positions = await UserPositionService.getUserPositionsByUserId(userId);
            setUserPositions(positions);
            
            // Don't reset form fields here - let handleInputChange handle it
            // This function is also called during edit mode setup
            return positions; // Return positions for immediate use
        } catch (error) {
            console.error('Error fetching user positions:', error);
            toast.error('Failed to load user positions');
            return [];
        }
    };

    // Update function to fetch unit type multiplier
    const fetchUnitTypeMultiplier = async (unitId) => {
        try {
            const position = userPositions.find(p => p.organizationalUnitId === parseInt(unitId));
            if (position) {
                // First get the organizational unit details to get the type ID
                const orgUnit = await OrganizationalUnitService.getUnitById(position.organizationalUnitId);
                if (orgUnit && orgUnit.unitTypeId) {
                    // Then get the unit type details to get the multiplier
                    const unitType = await OrganizationalUnitTypeService.getTypeById(orgUnit.unitTypeId);
                    if (unitType && unitType.multiplier) {
                        const updatedData = {
                            ...formData,
                            positionMultiplier: unitType.multiplier
                        };
                        updatedData.roleImportance = calculateRoleImportance(updatedData);
                        setFormData(updatedData);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching unit type multiplier:', error);
            toast.error('Failed to load unit type multiplier');
        }
    };

    // Add function to calculate role importance
    const calculateRoleImportance = (data) => {
        // Convert all values to numbers and ensure they exist
        const positionMult = Number(data.positionMultiplier || 1.0);
        const experienceMult = Number(data.experienceLevelMultiplier || 1.0);
        const educationMult = Number(data.educationlevelMultiplier || 1.0);
        const organizationalMult = Number(data.organizationalLevelMultiplier || 1.0);
        const impactMult = Number(data.impactFactor || 1.0);
        
        // Calculate the exact value
        const exactValue = positionMult * experienceMult * educationMult * organizationalMult * impactMult;
        
        // Return the value without any rounding or truncation
        return exactValue;
    };

    // Modal handlers
    const handleShowModal = async (appointment = null) => {
        if (appointment) {
            setSelectedAppointment(appointment);
            // First fetch user positions for the selected user
            const fetchedPositions = await fetchUserPositions(appointment.userId);
            
            // Determine position type based on organizational unit name
            const isNonOrganizational = appointment.organizationalUnitName === 'Non-Organizational';
            setPositionType(isNonOrganizational ? 'nonOrganizational' : 'organizational');
            
            let position = null;
            let nonOrgPosition = null;
            
            if (isNonOrganizational) {
                console.log('Debug: Processing non-organizational position');
                console.log('Target position title:', appointment.positionTitle);
                console.log('Available non-org positions:', nonOrgPositions.map(p => p.name));
                
                // For non-organizational positions, first try exact match
                nonOrgPosition = nonOrgPositions.find(p => 
                    p.name === appointment.positionTitle
                );
                
                console.log('Exact match found:', nonOrgPosition?.name || 'None');
                
                // If exact match not found, try to find closest match
                if (!nonOrgPosition && nonOrgPositions.length > 0) {
                    console.log('Trying fuzzy match...');
                    nonOrgPosition = findClosestNonOrgPosition(appointment.positionTitle, nonOrgPositions);
                    console.log('Fuzzy match found:', nonOrgPosition?.name || 'None');
                }
                
                if (nonOrgPosition) {
                    setSelectedNonOrgPosition(nonOrgPosition);
                    console.log('Selected non-org position:', nonOrgPosition.name);
                } else {
                    setSelectedNonOrgPosition(null);
                    console.log('No suitable non-org position found');
                }
                setSelectedPosition(null);
            } else {
                // For organizational positions, first find by organizational unit name
                const positionsInUnit = fetchedPositions.filter(p => 
                    p.organizationalUnitName === appointment.organizationalUnitName
                );
                
                if (positionsInUnit.length > 0) {
                    // Try to find exact match by position title within the unit
                    position = positionsInUnit.find(p => p.positionTitle === appointment.positionTitle);
                    
                    // If no exact position title match, use the first position in the unit
                    if (!position) {
                        position = positionsInUnit[0];
                    }
                    
                    setSelectedPosition(position);
                    // Fetch unit type multiplier
                    await fetchUnitTypeMultiplier(position.organizationalUnitId);
                } else {
                    setSelectedPosition(null);
                }
                setSelectedNonOrgPosition(null);
            }

            // Set form data with all appointment details
            setFormData({
                userId: appointment.userId.toString(),
                organizationalUnitName: appointment.organizationalUnitName,
                organizationalUnitId: position?.organizationalUnitId?.toString() || '',
                roleName: appointment.roleName || '',
                positionTitle: appointment.positionTitle || '',
                employmentType: appointment.employmentType || '',
                employmentTypeMultiplier: appointment.employmentTypeMultiplier || 1.0,
                positionMultiplier: appointment.positionMultiplier || 1.0,
                experienceLevelName: appointment.experienceLevelName || '',
                experienceLevelMultiplier: appointment.experienceLevelMultiplier || 1.0,
                educationLevelName: appointment.educationLevelName || '',
                educationlevelMultiplier: appointment.educationlevelMultiplier || 1.0,
                organizationalLevelName: appointment.organizationalLevelName || '',
                organizationalLevelMultiplier: appointment.organizationalLevelMultiplier || 1.0,
                roleImportance: appointment.roleImportance || 0,
                impactFactor: appointment.impactFactor || 1.0,
                xPortion: appointment.xPortion || latestXPortion?.value || 1.0,
                startDate: appointment.startDate?.split('T')[0] || '',
                endDate: appointment.endDate?.split('T')[0] || ''
            });
        } else {
            setSelectedAppointment(null);
            // Reset to default organizational position type for new appointments
            setPositionType('organizational');
            setFormData({
                userId: '',
                organizationalUnitName: '',
                organizationalUnitId: '',
                roleName: '',
                positionTitle: '',
                employmentType: '',
                employmentTypeMultiplier: 1.0,
                positionMultiplier: 1.0,
                experienceLevelName: '',
                experienceLevelMultiplier: 1.0,
                educationLevelName: '',
                educationlevelMultiplier: 1.0,
                organizationalLevelName: '',
                organizationalLevelMultiplier: 1.0,
                roleImportance: 0,
                impactFactor: 1.0,
                xPortion: latestXPortion?.value || 1.0,
                startDate: '',
                endDate: ''
            });
            setSelectedPosition(null);
            setSelectedNonOrgPosition(null);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAppointment(null);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'positionType') {
            setPositionType(value);
            // Reset related form fields when switching position types
            setFormData(prev => ({
                ...prev,
                organizationalUnitName: value === 'nonOrganizational' ? 'Non-Organizational' : '',
                positionTitle: '',
                positionMultiplier: 1.0,
                organizationalUnitId: ''
            }));
            setSelectedPosition(null);
            setSelectedNonOrgPosition(null);
        } else if (name === 'nonOrgPositionId') {
            const position = nonOrgPositions.find(p => p.id === parseInt(value));
            if (position) {
                setSelectedNonOrgPosition(position);
                const updatedData = {
                    ...formData,
                    organizationalUnitName: 'Non-Organizational',
                    positionTitle: position.name,
                    positionMultiplier: position.multiplier
                };
                updatedData.roleImportance = calculateRoleImportance(updatedData);
                setFormData(updatedData);
            }
        } else if (name === 'userId') {
            // When user is selected, fetch their positions but preserve position type
            fetchUserPositions(value);
            setFormData(prev => ({
                ...prev,
                userId: value,
                // Reset position-related fields but keep position type
                organizationalUnitName: positionType === 'nonOrganizational' ? 'Non-Organizational' : '',
                positionTitle: '',
                organizationalUnitId: '',
                positionMultiplier: 1.0
            }));
            // Reset selected positions
            setSelectedPosition(null);
            setSelectedNonOrgPosition(null);
        } else if (name === 'organizationalUnitId') {
            // When organizational unit is selected, set unit name and position details
            const position = userPositions.find(p => p.organizationalUnitId === parseInt(value));
            if (position) {
                setSelectedPosition(position);
                const updatedData = {
                    ...formData,
                    organizationalUnitId: value,
                    organizationalUnitName: position.organizationalUnitName,
                    positionTitle: position.positionTitle
                };
                updatedData.roleImportance = calculateRoleImportance(updatedData);
                setFormData(updatedData);
                // Fetch unit type multiplier and update role importance
                fetchUnitTypeMultiplier(value).then(() => {
                    const newData = {
                        ...updatedData,
                        positionMultiplier: formData.positionMultiplier
                    };
                    newData.roleImportance = calculateRoleImportance(newData);
                    setFormData(newData);
                });
            }
        } else if (name.includes('|')) {
            const [fieldName, multiplierField] = name.split('|');
            const [selectedValue, multiplier] = value.split('|');
            const updatedData = {
                ...formData,
                [fieldName]: selectedValue,
                [multiplierField]: parseFloat(multiplier)
            };
            updatedData.roleImportance = calculateRoleImportance(updatedData);
            setFormData(updatedData);
        } else if (name === 'impactFactor' || name === 'positionMultiplier') {
            const updatedData = {
                ...formData,
                [name]: parseFloat(value)
            };
            updatedData.roleImportance = calculateRoleImportance(updatedData);
            setFormData(updatedData);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Convert dates to UTC format
            const startDate = formData.startDate ? 
                new Date(formData.startDate + 'T00:00:00.000Z').toISOString() : null;
            const endDate = formData.endDate ? 
                new Date(formData.endDate + 'T00:00:00.000Z').toISOString() : null;

            const appointmentData = {
                userId: formData.userId,
                userFullName: getUserFullName(formData.userId),
                organizationalUnitName: formData.organizationalUnitName,
                positionTitle: formData.positionTitle,
                roleName: formData.roleName,
                employmentType: formData.employmentType,
                employmentTypeMultiplier: parseFloat(formData.employmentTypeMultiplier),
                positionMultiplier: parseFloat(formData.positionMultiplier),
                experienceLevelName: formData.experienceLevelName,
                experienceLevelMultiplier: parseFloat(formData.experienceLevelMultiplier),
                educationLevelName: formData.educationLevelName,
                educationlevelMultiplier: parseFloat(formData.educationlevelMultiplier),
                organizationalLevelName: formData.organizationalLevelName,
                organizationalLevelMultiplier: parseFloat(formData.organizationalLevelMultiplier),
                roleImportance: parseFloat(formData.roleImportance),
                impactFactor: parseFloat(formData.impactFactor),
                xPortion: parseFloat(formData.xPortion),
                startDate,
                endDate
            };
            
            if (selectedAppointment) {
                await AppointmentService.updateAppointment(selectedAppointment.id, appointmentData);
                toast.success('Appointment updated successfully');
            } else {
                await AppointmentService.addAppointment(appointmentData);
                toast.success('Appointment added successfully');
            }
            
            handleCloseModal();
            fetchData();
        } catch (error) {
            console.error('Error saving appointment:', error);
            toast.error('Failed to save appointment');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this appointment?')) {
            try {
                await AppointmentService.deleteAppointment(id);
                toast.success('Appointment deleted successfully');
                fetchData();
            } catch (error) {
                console.error('Error deleting appointment:', error);
                toast.error('Failed to delete appointment');
            }
        }
    };

    // Helper functions
    const getUserDetails = (userId) => {
        return users.find(user => user.id === userId) || {};
    };

    const getUserFullName = (userId) => {
        const user = getUserDetails(userId);
        return `${user.firstName} ${user.lastName}`;
    };

    // Define columns for ResponsiveTable
    const columns = [
        {
            key: 'user',
            label: 'User',
            render: (_, item) => {
                const user = getUserDetails(item.userId);
                return (
                    <div className="user-cell">
                        <ProfileImage
                            imageUrl={user.imageUrl}
                            width={40}
                            height={40}
                            className="me-2 rounded-circle"
                        />
                        <span>
                            {(() => {
                                if (user.firstName && user.lastName) {
                                    return `${user.firstName} ${user.lastName}`;
                                } else if (user.firstName) {
                                    return user.firstName;
                                } else if (user.lastName) {
                                    return user.lastName;
                                } else {
                                    return user.username || 'Unknown User';
                                }
                            })()}
                        </span>
                    </div>
                );
            }
        },
        { 
            key: 'organizationalUnitName',
            label: 'Unit'
        },
        { 
            key: 'positionTitle',
            label: 'Position' 
        },
        { 
            key: 'roleName',
            label: 'Role' 
        },
        { 
            key: 'employmentType',
            label: 'Type',
            hideOnMobile: true 
        },
        { 
            key: 'experienceLevelName',
            label: 'Experience',
            hideOnMobile: true,
            render: (value) => value || '-'
        },
        { 
            key: 'educationLevelName',
            label: 'Education',
            hideOnMobile: true,
            render: (value) => value || '-'
        },
        { 
            key: 'organizationalLevelName',
            label: 'Org. Level',
            hideOnMobile: true,
            render: (value) => value || '-'
        }
    ];

    // Define actions for ResponsiveTable
    const actions = [
        {
            icon: <BsPencil />,
            variant: 'outline-primary',
            onClick: (appointment) => handleShowModal(appointment)
        },
        {
            icon: <BsTrash />,
            variant: 'outline-danger',
            onClick: (appointment) => handleDelete(appointment.id)
        }
    ];

    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Button variant="primary" onClick={() => handleShowModal()}>
                    <BsPlusLg /> Add New Appointment
                </Button>
            </div>

            <ResponsiveTable 
                data={appointments} 
                columns={columns} 
                actions={actions}
            />

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedAppointment ? 'Edit Appointment' : 'Add Appointment'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>User</Form.Label>
                            <Form.Select
                                name="userId"
                                value={formData.userId}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select User</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` :
                                         user.lastName ? user.lastName :
                                         user.firstName ? user.firstName :
                                         user.username}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Position Type</Form.Label>
                            <div>
                                <Form.Check
                                    inline
                                    type="radio"
                                    name="positionType"
                                    id="organizational"
                                    label="Organizational Unit"
                                    value="organizational"
                                    checked={positionType === 'organizational'}
                                    onChange={handleInputChange}
                                />
                                <Form.Check
                                    inline
                                    type="radio"
                                    name="positionType"
                                    id="nonOrganizational"
                                    label="Non-Organizational Position"
                                    value="nonOrganizational"
                                    checked={positionType === 'nonOrganizational'}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </Form.Group>

                        {positionType === 'organizational' ? (
                            <Form.Group className="mb-3">
                                <Form.Label>Organizational Unit</Form.Label>
                                <Form.Select
                                    name="organizationalUnitId"
                                    value={selectedPosition?.organizationalUnitId || ''}
                                    onChange={handleInputChange}
                                    required
                                    disabled={!formData.userId || userPositions.length === 0}
                                >
                                    <option value="">Select Unit</option>
                                    {userPositions.map(position => (
                                        <option key={position.organizationalUnitId} value={position.organizationalUnitId}>
                                            {position.organizationalUnitName}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>Non-Organizational Position</Form.Label>
                                <Form.Select
                                    name="nonOrgPositionId"
                                    value={selectedNonOrgPosition?.id || ''}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Position</option>
                                    {nonOrgPositions.map(position => (
                                        <option key={position.id} value={position.id}>
                                            {position.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Position Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="positionTitle"
                                        value={formData.positionTitle}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="roleName"
                                        value={formData.roleName}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Employment Type</Form.Label>
                                    <Form.Select
                                        name="employmentType|employmentTypeMultiplier"
                                        value={formData.employmentType && formData.employmentTypeMultiplier ? 
                                            `${formData.employmentType}|${formData.employmentTypeMultiplier}` : ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Employment Type</option>
                                        {employmentTypes.map(type => (
                                            <option key={type.id} value={`${type.name}|${type.multiplier}`}>
                                                {type.name} (x{type.multiplier})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Position Multiplier</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="positionMultiplier"
                                        value={formData.positionMultiplier}
                                        disabled
                                    />
                                    <Form.Text className="text-muted">
                                        Based on unit type multiplier
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Experience Level</Form.Label>
                                    <Form.Select
                                        name="experienceLevelName|experienceLevelMultiplier"
                                        value={formData.experienceLevelName && formData.experienceLevelMultiplier ? 
                                            `${formData.experienceLevelName}|${formData.experienceLevelMultiplier}` : ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Experience Level</option>
                                        {experienceLevels.map(level => (
                                            <option key={level.id} value={`${level.name}|${level.multiplier}`}>
                                                {level.name} (x{level.multiplier})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Education Level</Form.Label>
                                    <Form.Select
                                        name="educationLevelName|educationlevelMultiplier"
                                        value={formData.educationLevelName && formData.educationlevelMultiplier ? 
                                            `${formData.educationLevelName}|${formData.educationlevelMultiplier}` : ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Education Level</option>
                                        {educationLevels.map(level => (
                                            <option key={level.id} value={`${level.name}|${level.multiplier}`}>
                                                {level.name} (x{level.multiplier})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Organizational Level</Form.Label>
                                    <Form.Select
                                        name="organizationalLevelName|organizationalLevelMultiplier"
                                        value={formData.organizationalLevelName && formData.organizationalLevelMultiplier ? 
                                            `${formData.organizationalLevelName}|${formData.organizationalLevelMultiplier}` : ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Organizational Level</option>
                                        {organizationalLevels.map(level => (
                                            <option key={level.id} value={`${level.name}|${level.multiplier}`}>
                                                {level.name} (x{level.multiplier})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col>
                                {/* Empty column for balance */}
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="roleImportance">Role Importance</label>
                                    <input
                                        type="number"
                                        id="roleImportance"
                                        className="form-control"
                                        value={formData.roleImportance?.toFixed(2) || '0.00'}
                                        disabled
                                        step="0.01"
                                    />
                                    <small className="text-muted">
                                        Exact calculation based on position, experience, education, organizational level, and impact factor
                                    </small>
                                </div>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Impact Factor</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="impactFactor"
                                        value={formData.impactFactor}
                                        onChange={handleInputChange}
                                        step="0.1"
                                        min="0"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>X-Portion Value</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.xPortion}
                                disabled
                            />
                            <Form.Text className="text-muted">
                                Latest X-Portion value from the system
                            </Form.Text>
                        </Form.Group>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                        min={formData.startDate}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-2" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                {selectedAppointment ? 'Update' : 'Add'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Appointment; 